import apiClient from "../client";

export const getInvestmentLeagueUser = async () => {
  const response = await apiClient.get("/investmentleague/user");
  return response.data;
};

export const getMacAndGroups = async () => {
  const response = await apiClient.get("/investmentleague/macandgroups");
  return response.data;
};

export const getInvestments = async () => {
  const response = await apiClient.get("/investmentleague/investments");
  return response.data;
};

export const getInvestmentsByMac = async (mac) => {
  const response = await apiClient.post("/investmentleague/investments/mac", { mac });
  return response.data;
};

export const getJackpotSummary = async () => {
  const response = await apiClient.get("/investmentleague/jackpot/summary");
  return response.data;
};

export const getJackpotSummaryByMac = async (mac) => {
  const response = await apiClient.post("/investmentleague/jackpot/mac/summary", { mac });
  return response.data;
};

export const getJackpotLeaderboard = async () => {
  const response = await apiClient.get("/investmentleague/jackpot/leaderboard");
  return response.data;
};

export const getLeagueLeaderboard = async () => {
  const response = await apiClient.get("/investmentleague/leagueleaderboard");
  return response.data;
};

export const getInvestmentRecipientLeaderboard = async () => {
  const response = await apiClient.get("/investmentleague/investment-recipient-leaderboard");
  return response.data;
};

export const getReinvestmentPreview = async () => {
  try {
    const response = await apiClient.get("/investmentleague/reinvestment/preview");
    return response.data;
  } catch (err) {
    if (err.response?.status === 404) {
      try {
        const fallbackRes = await apiClient.get("/investmentleague/reinvestment");
        return fallbackRes.data;
      } catch {
        throw err;
      }
    }
    throw err;
  }
};

export const submitReinvestment = async (payload) => {
  const response = await apiClient.post("/investmentleague/reinvestment", payload);
  return response.data;
};

/**
 * @typedef {Object} ReinvestmentCreditValidationResponse
 * @property {string} investorUnit
 * @property {string} previousQuarter
 * @property {string} upcomingQuarter
 * @property {number} previousQuarterCredit
 * @property {number} allocatedAmount
 * @property {number} remainingCredit
 * @property {number} allocationPercentage
 * @property {boolean} isFullyAllocated
 * @property {boolean} isOverAllocated
 * @property {boolean} isValid
 */

/**
 * Fetches server-calculated credit validation for a given quarter.
 * Authoritative source of truth for previous quarter credit, Q3 allocated amount,
 * remaining credit, and allocation state.
 *
 * @param {string} [quarter="Q3"]
 * @returns {Promise<ReinvestmentCreditValidationResponse>}
 */
export const getReinvestmentCreditValidation = async (quarter = "Q3") => {
  const response = await apiClient.get("/investmentleague/reinvestment/credit-validation", {
    params: { quarter },
  });
  return response.data;
};

/**
 * @typedef {Object} ScreenConstraintResponse
 * @property {string} screenName
 * @property {boolean} openAccess
 */

/**
 * Fetches screen access constraint for a given screen name.
 * Server-side source of truth for whether the screen is open or locked.
 *
 * @param {string} [screenName="Re-invest for Q3"]
 * @returns {Promise<ScreenConstraintResponse>}
 */
export const getScreenConstraint = async (screenName = "Re-invest for Q3") => {
  const response = await apiClient.get("/investmentleague/screen-constraint", {
    params: { screenName },
  });
  return response.data;
};

/**
 * Updates screen constraint to open access for a given screen name.
 *
 * @param {string} [screenName="Re-invest for Q3"]
 * @param {boolean} [openAccess=true]
 * @returns {Promise<ScreenConstraintResponse>}
 */
export const openScreenAccess = async (screenName = "Re-invest for Q3", openAccess = true) => {
  const response = await apiClient.post("/investmentleague/screen-constraint", {
    screenName,
    openAccess,
  });
  return response.data;
};

/**
 * Updates screen constraint to restrict/close access for a given screen name.
 *
 * @param {string} [screenName="Re-invest for Q3"]
 * @returns {Promise<ScreenConstraintResponse>}
 */
export const restrictScreenAccess = async (screenName = "Re-invest for Q3") => {
  const response = await apiClient.post("/investmentleague/screen-constraint", {
    screenName,
    openAccess: false,
  });
  return response.data;
};



